import * as React from "react";
import { Authenticated } from "convex/react";
import { useMutation } from "convex/react";
import { useQuery } from "convex-helpers/react/cache/hooks";
import { DropdownMenu, Spinner } from "@radix-ui/themes";
import {
  ArchiveIcon,
  ArrowLeftIcon,
  ChatBubbleIcon,
  DotsHorizontalIcon,
  ExternalLinkIcon,
  Pencil2Icon,
  ResetIcon,
} from "@radix-ui/react-icons";
import Markdown from "react-markdown";
import { toast } from "sonner";
import { api } from "@repo/convex/convex/_generated/api";
import { Doc, Id } from "@repo/convex/convex/_generated/dataModel";
import { routes } from "../routing/router.ts";
import { useAppearance } from "../app/appearance.ts";
import { useCommonDialogs } from "../common/CommonDialogsProvider.tsx";
import { useOptimisticUnArchiveItem } from "../common/hooks.ts";
import { itemKind } from "../common/items/itemKind.ts";
import {
  domain,
  itemTitle,
  minutesLeft,
  readingMinutes,
  siteLineColor,
  videoLength,
} from "../common/items/format.ts";
import { ItemPicture, PlayMark, SiteIcon } from "../common/items/ItemPictures.tsx";
import { CardIconButton } from "../common/items/ItemActions.tsx";
import { CopyLinkMenuItem } from "../common/items/menu/CopyLinkMenuItem.tsx";
import { ItemDetailsMenuItem } from "../common/items/menu/ItemDetailsMenuItem.tsx";
import { TrashItemMenuItem } from "../common/items/menu/TrashItemMenuItem.tsx";
import { PermanentlyDeleteMenuItem } from "../trash/PermanentlyDeleteMenuItem.tsx";
import { readerColors, textSteps, useReaderSettings } from "./readerSettings.ts";
import { ReaderSettingsPopover } from "./ReaderSettingsPopover.tsx";
import "./reader.css";

// The reader: the saved copy of an article on a plain page, with a slim bar and good type, so the
// reading's the thing. Like the app's (apps/ios/StashIt/Features/Reader/ReaderView.swift), it
// remembers how far through you got, on the server, so the phone opens where the web left off and
// the other way round. It opens over the list you came from, which waits underneath.
//
// The app can also show the live web page. A browser can't: most sites won't be put in a frame,
// so "Open the original" is always one click away instead.

interface Props {
  itemId: string;
  /** The list underneath, for the back button's label and for going back to it */
  backTo: "stash" | "archive" | "trash" | "settings";
  /** You got here from that list in this tab, so going back is the browser's back */
  cameFromList: boolean;
}

const backLabels: Record<Props["backTo"], string> = {
  stash: "The Stash",
  archive: "Archive",
  trash: "Trash",
  settings: "Settings",
};

export const ReaderPage: React.FC<Props> = (props) => {
  const { scheme } = useAppearance();
  const settings = useReaderSettings();
  const colors = readerColors(settings.page, scheme);

  const close = () => {
    if (props.cameFromList) window.history.back();
    else routes[props.backTo]().replace();
  };

  // Escape goes back to the list, unless it's closing a dialog or menu first
  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key != "Escape" || event.defaultPrevented) return;
      if (document.querySelector('[role="dialog"], [role="menu"], [role="alertdialog"]')) return;
      close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props.cameFromList, props.backTo]);

  return (
    <div
      className={`reader ${colors.isDark ? "reader-dark" : "reader-light"} ${settings.sans ? "reader-sans" : ""} reader-width-${settings.width}`}
      style={
        {
          "--page": colors.page,
          "--ink": colors.ink,
          "--ink2": colors.ink2,
          "--link": colors.link,
          "--line": colors.isDark ? "rgba(228, 222, 211, 0.16)" : "rgba(42, 37, 31, 0.12)",
          "--text-scale": textSteps[Math.min(Math.max(settings.step, 0), textSteps.length - 1)] ?? 1,
          colorScheme: colors.isDark ? "dark" : "light",
        } as React.CSSProperties
      }
      role="main"
    >
      <Authenticated>
        <ItemErrorBoundary key={props.itemId} onBack={close} backLabel={backLabels[props.backTo]}>
          <Reader
            itemId={props.itemId as Id<"items">}
            onBack={close}
            backLabel={backLabels[props.backTo]}
            isDark={colors.isDark}
          />
        </ItemErrorBoundary>
      </Authenticated>
    </div>
  );
};

interface ReaderProps {
  itemId: Id<"items">;
  onBack: () => void;
  backLabel: string;
  isDark: boolean;
}

const Reader: React.FC<ReaderProps> = ({ itemId, onBack, backLabel, isDark }) => {
  const item = useQuery(api.items.findMine, { itemId });
  const content = useQuery(api.itemContent.user.queries.findMineForItem, { itemId });
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const progress = useReadingPosition({ item, content, scrollRef });

  React.useEffect(() => {
    scrollRef.current?.focus({ preventScroll: true });
  }, []);

  if (item === null) return <Gone onBack={onBack} backLabel={backLabel} />;

  const markdown = content?.status.kind == "scraped" && content.content.trim() ? content.content : null;
  // A video's saved copy is its transcript, so it's the video's own length that means something
  const isVideo = item ? itemKind(item.url).kind == "video" : false;
  const minutes = markdown && !isVideo ? readingMinutes(markdown.length) : null;
  const length = isVideo ? videoLength(item?.videoSeconds) : null;
  const left =
    minutes == null ? null : progress > 0.98 ? "The end" : `${minutesLeft(minutes, progress)} min left`;
  const siteDomain = item ? domain(item.url) : "";

  return (
    <>
      <header className="reader-bar">
        <button type="button" className="reader-back" onClick={onBack} aria-label={`Back to ${backLabel}`}>
          <ArrowLeftIcon />
          <span className="only-desktop">{backLabel}</span>
        </button>
        <div className="reader-left" aria-live="polite">
          {left}
        </div>
        <div className="reader-bar-end">
          {item && (
            <div className="reader-tools only-desktop">
              <ReaderTools item={item} onGone={onBack} />
            </div>
          )}
          <ReaderSettingsPopover />
        </div>
        <div className="reader-progress" aria-hidden>
          <span
            style={{
              width: `${(markdown ? progress : 0) * 100}%`,
              background: siteLineColor(siteDomain, isDark),
            }}
          />
        </div>
      </header>

      <div className="reader-scroll" ref={scrollRef} tabIndex={-1}>
        {item === undefined ? (
          <div className="reader-loading">
            <Spinner size="3" />
          </div>
        ) : (
          <div className="reader-sheet">
            <VideoAtTop item={item} />
            <h1 className="reader-title">{itemTitle(item)}</h1>
            <p className="reader-meta">
              <SiteIcon item={item} size={16} />
              <a href={item.url} target="_blank" rel="noopener noreferrer">
                {siteDomain}
              </a>
              {(minutes != null || length) && (
                <>
                  <span aria-hidden>·</span>
                  <span>{length ?? `${minutes} min`}</span>
                </>
              )}
            </p>
            <div className="reader-rule" />
            {markdown ? (
              <ReaderArticle markdown={markdown} title={item.title} />
            ) : (
              <NoSavedCopy item={item} content={content} />
            )}
            <ReaderEnd item={item} />
          </div>
        )}
      </div>

      {item && (
        <nav className="reader-tools reader-tools-bottom only-phone" aria-label="Item">
          <ReaderTools item={item} onGone={onBack} />
        </nav>
      )}
    </>
  );
};

/**
 * Where you've got to in the article, 0 to 1. Opens where you left off (on any device) and saves
 * as you read, but not on every scroll: when it's moved a couple of percent and a few seconds have
 * passed, and when you leave. Last writer wins on `at`, like the app.
 */
const useReadingPosition = ({
  item,
  content,
  scrollRef,
}: {
  item: Doc<"items"> | null | undefined;
  content: Doc<"itemContent"> | null | undefined;
  scrollRef: React.RefObject<HTMLDivElement>;
}) => {
  const setReadingPosition = useMutation(api.items.setReadingPosition);
  const [progress, setProgress] = React.useState(0);
  const ready = item != null && content?.status.kind == "scraped" && !!content.content.trim();
  const restored = React.useRef(false);
  const lastSaved = React.useRef({ position: 0, at: 0 });
  const latest = React.useRef(0);
  const itemId = item?._id;

  const measure = () => {
    const el = scrollRef.current;
    if (!el) return 0;
    const max = el.scrollHeight - el.clientHeight;
    return max <= 0 ? 1 : Math.min(1, Math.max(0, el.scrollTop / max));
  };

  const save = (position: number) => {
    if (!itemId) return;
    lastSaved.current = { position, at: Date.now() };
    setReadingPosition({ itemId, position, at: Date.now() }).catch(console.error);
  };

  // Back to where you were, once the article's on the page
  React.useLayoutEffect(() => {
    if (!ready || restored.current || !item) return;
    restored.current = true;
    const start = item.readingPosition ?? 0;
    lastSaved.current = { position: start, at: Date.now() };
    latest.current = start;
    const el = scrollRef.current;
    if (el && start > 0 && start < 0.98)
      el.scrollTop = start * (el.scrollHeight - el.clientHeight);
    setProgress(measure());
  }, [ready]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || !ready) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const position = measure();
        latest.current = position;
        setProgress(position);
        const moved = Math.abs(position - lastSaved.current.position) >= 0.02;
        const waited = Date.now() - lastSaved.current.at > 3000;
        if (restored.current && moved && waited) save(position);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("scroll", onScroll);
    };
  }, [ready, itemId]);

  // And once more on the way out, if it's moved since the last save
  React.useEffect(
    () => () => {
      if (restored.current && Math.abs(latest.current - lastSaved.current.position) >= 0.005)
        save(latest.current);
    },
    [itemId],
  );

  return progress;
};

/** The article itself. Scrapes usually start with the page's own heading, which the header has. */
const ReaderArticle: React.FC<{ markdown: string; title: string | null }> = React.memo(
  ({ markdown, title }) => {
    const body = React.useMemo(() => withoutRepeatedTitle(markdown, title), [markdown, title]);
    return (
      <article className="reader-article">
        <Markdown
          skipHtml
          components={{
            a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
            img: ({ node, ...props }) => (
              <img {...props} loading="lazy" decoding="async" referrerPolicy="no-referrer" />
            ),
          }}
        >
          {body}
        </Markdown>
      </article>
    );
  },
);

const withoutRepeatedTitle = (markdown: string, title: string | null) => {
  const match = /^\s*#{1,3}\s+(.+)\n/.exec(markdown);
  if (!match || !title) return markdown;
  const heading = (match[1] ?? "").trim().toLowerCase();
  return heading && title.toLowerCase().startsWith(heading)
    ? markdown.slice(match[0].length)
    : markdown;
};

/** A YouTube video plays at the top, its transcript (the saved copy) underneath. */
const VideoAtTop: React.FC<{ item: Doc<"items"> }> = ({ item }) => {
  const kind = itemKind(item.url);
  if (kind.kind != "video") return null;
  if (kind.youTubeId)
    return (
      <div className="reader-video">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${kind.youTubeId}`}
          title={itemTitle(item)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  // Other video sites won't play in a frame here, so it's their picture, off to the site
  return (
    <a className="reader-video" href={item.url} target="_blank" rel="noopener noreferrer">
      <ItemPicture item={item} className="reader-video-picture" />
      <PlayMark />
    </a>
  );
};

/** Why there's no article to read, and the original instead. */
const NoSavedCopy: React.FC<{
  item: Doc<"items">;
  content: Doc<"itemContent"> | null | undefined;
}> = ({ item, content }) => {
  if (content === undefined)
    return (
      <div className="reader-loading">
        <Spinner size="3" />
      </div>
    );
  const failed =
    content?.status.kind == "scraping_error" ||
    (content?.status.kind == "scraped" && !content.content.trim());
  return (
    <div className="reader-empty">
      <p>
        {failed
          ? "Couldn't fetch this one. Open the original instead."
          : "Still fetching this one. Give it a minute."}
      </p>
      <a className="paper-button" href={item.url} target="_blank" rel="noopener noreferrer">
        <ExternalLinkIcon /> Open the original
      </a>
    </div>
  );
};

/** At the bottom of the article: done with it? */
const ReaderEnd: React.FC<{ item: Doc<"items"> }> = ({ item }) => {
  const dialogs = useCommonDialogs();
  return (
    <footer className="reader-end">
      {item.status.kind == "unread" && (
        <button
          type="button"
          className="paper-button"
          onClick={() => dialogs?.openPostArchive({ itemId: item._id })}
        >
          <ArchiveIcon /> Archive it
        </button>
      )}
      <a className="paper-button" href={item.url} target="_blank" rel="noopener noreferrer">
        <ExternalLinkIcon /> Open the original
      </a>
    </footer>
  );
};

/** Archive (or back on the pile), notes, chat, the original and the rest. */
const ReaderTools: React.FC<{ item: Doc<"items">; onGone: () => void }> = ({ item, onGone }) => {
  const dialogs = useCommonDialogs();
  const unarchive = useOptimisticUnArchiveItem();
  const restore = useMutation(api.items.restoreMine);
  return (
    <>
      {item.status.kind == "unread" && (
        <CardIconButton label="Archive" onClick={() => dialogs?.openPostArchive({ itemId: item._id })}>
          <ArchiveIcon />
        </CardIconButton>
      )}
      {item.status.kind == "archived" && (
        <CardIconButton
          label="Back on the pile"
          onClick={() =>
            unarchive({ itemId: item._id })
              .then(() => toast.success("Back on the pile"))
              .catch(toast.error)
          }
        >
          <ResetIcon />
        </CardIconButton>
      )}
      {item.status.kind == "trashed" && (
        <CardIconButton
          label="Restore"
          onClick={() =>
            restore({ itemId: item._id })
              .then(() => toast.success("Restored"))
              .catch(toast.error)
          }
        >
          <ResetIcon />
        </CardIconButton>
      )}
      <CardIconButton label="Notes" onClick={() => dialogs?.openItemNotesEditor({ itemId: item._id })}>
        <Pencil2Icon />
      </CardIconButton>
      <CardIconButton label="Chat about it" onClick={() => dialogs?.openItemChat({ itemId: item._id })}>
        <ChatBubbleIcon />
      </CardIconButton>
      <a
        className="icon-button"
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open the original"
        title="Open the original"
      >
        <ExternalLinkIcon />
      </a>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger>
          <CardIconButton label="More">
            <DotsHorizontalIcon />
          </CardIconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" style={{ minWidth: "190px" }}>
          <CopyLinkMenuItem itemUrl={item.url} />
          <ItemDetailsMenuItem itemId={item._id} />
          <DropdownMenu.Separator />
          {item.status.kind == "trashed" ? (
            <PermanentlyDeleteMenuItem item={item} />
          ) : (
            <TrashItemMenuItem item={item} onTrashed={onGone} />
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </>
  );
};

const Gone: React.FC<{ onBack: () => void; backLabel: string }> = ({ onBack, backLabel }) => (
  <div className="reader-gone">
    <img src="/logo.webp" alt="" width={110} height={110} />
    <h1>That one's gone</h1>
    <p>It may have been deleted, or it's someone else's.</p>
    <button type="button" className="paper-button" onClick={onBack}>
      <ArrowLeftIcon /> Back to {backLabel}
    </button>
  </div>
);

/** A link to an item that isn't yours (or isn't an item at all) makes the query throw. */
class ItemErrorBoundary extends React.Component<
  { children: React.ReactNode; onBack: () => void; backLabel: string },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) return <Gone onBack={this.props.onBack} backLabel={this.props.backLabel} />;
    return this.props.children;
  }
}
